/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { createContext, useContext, useEffect, useState } from "react";
import { DataGridInner } from "./DataGridInner";
import { TDataGridProps, TDataGridSelectedRowIds } from "./DataGrid";
import { deepMerge } from "@/utils";
import type { SortingState, Table } from "@tanstack/react-table";

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonArray = JsonValue[];
interface JsonObject {
  [key: string]: JsonValue;
}

export interface IDataGridContextProps<TData extends object> {
  props: TDataGridProps<TData>;
  table: Table<TData>;
  totalRows: number;
  loading: boolean;
  setLoading: (state: boolean) => void;
  selectedRowIds: Set<string>;
  toggleRowSelection: (id: string) => void;
  toggleAllRowsSelection: (checked: boolean) => void;
  getSelectedRowIds: () => string[];
  isSelectAllChecked: boolean;
  isSelectAllIndeterminate: boolean;
  resetFilters: () => void; // Added resetFilters method
}

// Create a context with a generic type parameter
const DataGridContext = createContext<IDataGridContextProps<any> | undefined>(
  undefined,
);
export function useDataGrid<TData extends object>() {
  const context = useContext(DataGridContext);
  if (!context) {
    throw new Error("useDataGrid must be used within a DataGridProvider");
  }
  return context as IDataGridContextProps<TData>;
}

export const DataGridProvider = <TData extends object>(
  props: TDataGridProps<TData>,
) => {
  const defaultValues: Partial<TDataGridProps<TData>> = {
    messages: {
      empty: "No data available",
      loading: "Loading...",
    },
    pagination: {
      info: "{from} - {to} of {count}",
      sizes: [1, 5, 10, 25, 50, 100],
      sizesLabel: "Show",
      sizesDescription: "per page",
      size: 5,
      page: 0,
      moreLimit: 5,
      more: false,
    },
    layout: { cellsBorder: true },
    rowSelect: false,
    serverSide: false,
  };

  const mergedProps = deepMerge(
    defaultValues as unknown as JsonObject,
    props as unknown as JsonObject,
  ) as unknown as TDataGridProps<TData>;

  const [data, setData] = useState<TData[]>(
    Array.isArray(mergedProps.data) ? (mergedProps.data as TData[]) : [],
  );
  const [loading, setLoading] = useState<boolean>(!!mergedProps.loading);
  const [totalRows, setTotalRows] = useState<number>(
    // Use totalItems from props if available (for server-side), otherwise use data.length
    props.pagination?.totalItems !== undefined
      ? props.pagination.totalItems
      : Array.isArray(mergedProps.data)
        ? mergedProps.data.length
        : 0,
  );

  useEffect(() => {
    setLoading(!!mergedProps.loading);
  }, [mergedProps.loading]);

  // Update totalRows when totalItems changes
  useEffect(() => {
    if (props.pagination?.totalItems !== undefined) {
      setTotalRows(props.pagination.totalItems);
    } else if (Array.isArray(mergedProps.data)) {
      setTotalRows(mergedProps.data.length);
    }
  }, [props.pagination?.totalItems, mergedProps.data]);

  // Keep internal data in sync with latest props.data
  useEffect(() => {
    setData(
      Array.isArray(mergedProps.data) ? (mergedProps.data as TData[]) : [],
    );
  }, [mergedProps.data]);

  // State management for selected rows
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [isSelectAllIndeterminate, setIsSelectAllIndeterminate] =
    useState<boolean>(false);

  // Pagination and Sorting from props
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: props.pagination?.page ?? 0,
    pageSize: props.pagination?.size ?? 5,
  });

  // Update internal pagination state if external pagination changes
  useEffect(() => {
    if (
      props.pagination?.page !== undefined &&
      props.pagination.page !== pagination.pageIndex
    ) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: props.pagination?.page ?? 0,
      }));
    }
  }, [props.pagination?.page]);

  // Update internal page size if external page size changes
  useEffect(() => {
    if (
      props.pagination?.size !== undefined &&
      props.pagination.size !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        pageSize: props.pagination?.size ?? 5,
      }));
    }
  }, [props.pagination?.size]);

  const [sorting, setSorting] = useState<SortingState>(
    Array.isArray(mergedProps.sorting)
      ? mergedProps.sorting.map((s) => ({
          id: s.id,
          desc: typeof s.desc === "boolean" ? s.desc : false,
        }))
      : [],
  );
  const [filters, setFilters] = useState<ColumnFiltersState>(
    mergedProps.filters ?? [],
  );

  // Define the column filters change handler that's compatible with OnChangeFn<ColumnFiltersState>
  const handleColumnFiltersChange = (
    updaterOrValue:
      | ColumnFiltersState
      | ((old: ColumnFiltersState) => ColumnFiltersState),
  ) => {
    // Apply the update to filters
    setFilters(updaterOrValue);

    // Schedule a reset of pagination to first page when filters change
    // This will be processed in the next render via the useEffect below
  };

  // Watch for filter changes and reset to first page when they change
  useEffect(() => {
    if (filters.length > 0 && pagination.pageIndex !== 0) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
    }
  }, [filters]);

  const table = useReactTable<TData>({
    columns: mergedProps.columns,
    data: data,
    debugTable: false,
    pageCount: mergedProps.serverSide
      ? Math.ceil(totalRows / pagination.pageSize)
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: mergedProps.serverSide,
    manualSorting: mergedProps.serverSide,
    manualFiltering: mergedProps.serverSide,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: handleColumnFiltersChange,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
    },
  });

  const toggleRowSelection = (id: string) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected: TDataGridSelectedRowIds = new Set(prevSelected);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      if (props.onRowsSelectChange) {
        props.onRowsSelectChange(newSelected);
      }
      return newSelected;
    });
  };

  const toggleAllRowsSelection = (checked: boolean) => {
    const allRowIds = table.getRowModel().rows.map((row) => row.id);
    const newSelectedRowIds: TDataGridSelectedRowIds = checked
      ? new Set(allRowIds)
      : new Set();
    setSelectedRowIds(newSelectedRowIds);
    if (props.onRowsSelectChange) {
      props.onRowsSelectChange(newSelectedRowIds);
    }
  };

  const getSelectedRowIds = () => {
    return Array.from(selectedRowIds);
  };

  const handleStateChange = () => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  };

  useEffect(() => {
    if (!mergedProps.serverSide && table.getState().sorting.length > 0) {
      const cleanup = handleStateChange();
      return cleanup; // Ensure cleanup is returned
    }
  }, [table.getState().sorting, mergedProps.serverSide]);

  useEffect(() => {
    if (!mergedProps.serverSide) {
      const cleanup = handleStateChange();
      return cleanup; // Ensure cleanup is returned
    }
  }, [table.getState().pagination, mergedProps.serverSide]);

  useEffect(() => {
    const allRowIds = table.getRowModel().rows.map((row) => row.id);
    const isAllSelected = allRowIds.every((id) => selectedRowIds.has(id));
    const isSomeSelected = allRowIds.some((id) => selectedRowIds.has(id));

    setIsSelectAllChecked(isAllSelected);
    setIsSelectAllIndeterminate(!isAllSelected && isSomeSelected);
  }, [selectedRowIds, table.getRowModel().rows]);

  // Reset filters to the initial state and trigger callback if provided
  const resetFilters = () => {
    // Reset column filters to initial state (usually empty)
    setFilters(mergedProps.filters ?? []);

    // Reset to first page
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

    // Call the onFilterReset callback if provided
    if (props.onFilterReset) {
      props.onFilterReset();
    }
  };

  return (
    <DataGridContext.Provider
      value={{
        props: mergedProps,
        table,
        totalRows,
        loading,
        setLoading,
        selectedRowIds,
        toggleRowSelection,
        toggleAllRowsSelection,
        getSelectedRowIds,
        isSelectAllChecked,
        isSelectAllIndeterminate,
        resetFilters, // Provide the resetFilters method
      }}
    >
      <DataGridInner />
    </DataGridContext.Provider>
  );
};
